import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowPathIcon, LinkIcon, PlusCircleIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline';
import WhatsAppConnectionModal from '../components/WhatsAppConnectionModal';
import WhatsAppInstanceDetailModal from '../components/WhatsAppInstanceDetailModal';
import WhatsAppWebhookModal from '../components/WhatsAppWebhookModal';
import { getBearerHeaders } from '../lib/auth';

const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net').replace(/\/$/, '');
const API_TIMEOUT_MS = 90000;

function authHeaders() {
  const bearer = getBearerHeaders();
  if (!bearer) return { timeout: API_TIMEOUT_MS };
  return { headers: bearer, timeout: API_TIMEOUT_MS };
}

function normalizeRole(role) {
  if (!role) return 'client';
  const r = String(role).toLowerCase();
  if (r === 'admin' || r === 'super_admin') return 'super_admin';
  if (r === 'white_label_admin') return 'white_label_admin';
  return r;
}

const FULL_SUPER_ADMIN_EMAILS = ['admin@siriusys.com.br'];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function isFullSuperAdminUser(userData, apiFlag) {
  if (apiFlag === true) return true;
  const email = String(userData?.email || '')
    .trim()
    .toLowerCase();
  return FULL_SUPER_ADMIN_EMAILS.includes(email);
}

function formatBrl(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
}

function billingPriceLabel(billing) {
  if (billing?.price == null) return null;
  const cycle = billing.billingCycleLabel || 'mês';
  return `${formatBrl(billing.price)}/${cycle}`;
}

function clientLabel(c) {
  if (!c) return '—';
  const nome = c.nome || 'Sem nome';
  const email = c.email ? ` (${c.email})` : '';
  const wl = c.whiteLabelNome ? ` · ${c.whiteLabelNome}` : '';
  return `${nome}${email}${wl}`;
}

const ROLE_LABEL = {
  super_admin: 'super admin',
  admin: 'admin',
  white_label_admin: 'white label',
  cliente: 'cliente',
  client: 'cliente'
};

function userLabel(u) {
  if (!u) return '—';
  const nome = u.nome || 'Sem nome';
  const email = u.email ? ` (${u.email})` : '';
  const role = u.role ? ` · ${ROLE_LABEL[String(u.role).toLowerCase()] || u.role}` : '';
  return `${nome}${email}${role}`;
}

function SuperAdminInstanciasView() {
  const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || '{}'), []);
  const effRole = normalizeRole(userData.role);
  const [isFullSuperAdmin, setIsFullSuperAdmin] = useState(() =>
    isFullSuperAdminUser(userData, null)
  );
  const isSuperAdmin = isFullSuperAdmin;
  const isWlAdmin = !isFullSuperAdmin && effRole === 'white_label_admin';
  // Quem pode provisionar instância grátis (sem Asaas): super admin e white label admin.
  const podeCriarGratis = isSuperAdmin || isWlAdmin;

  const [tab, setTab] = useState('instancias');
  const [instancias, setInstancias] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [whiteLabels, setWhiteLabels] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroSearch, setFiltroSearch] = useState('');
  const [filtroClienteId, setFiltroClienteId] = useState('');
  const [filtroWlId, setFiltroWlId] = useState('');
  const [filtroDataIni, setFiltroDataIni] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroToken, setFiltroToken] = useState('');

  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [catalogMeta, setCatalogMeta] = useState({ total: 0, totalPages: 1, page: 1 });

  const [novaNome, setNovaNome] = useState('');
  const [clienteCheckoutId, setClienteCheckoutId] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [syncPayLoadingId, setSyncPayLoadingId] = useState(null);
  const [connectionModal, setConnectionModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [webhookModal, setWebhookModal] = useState(null);
  const [detailActionLoading, setDetailActionLoading] = useState(false);
  const [linkModal, setLinkModal] = useState(null);
  const [linkClienteId, setLinkClienteId] = useState('');
  const [linkNome, setLinkNome] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [billing, setBilling] = useState(null);

  // Super admin: criar instância para um admin + associar a qualquer usuário.
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [adminCreateId, setAdminCreateId] = useState('');
  const [adminCreateNome, setAdminCreateNome] = useState('');
  const [adminCreateLoading, setAdminCreateLoading] = useState(false);
  const [selfCreateNome, setSelfCreateNome] = useState('');
  const [selfCreateLoading, setSelfCreateLoading] = useState(false);

  const rawRole = String(userData.role || '').toLowerCase();
  const pode =
    effRole === 'super_admin' || effRole === 'white_label_admin' || rawRole === 'admin';

  const carregarClientes = useCallback(async () => {
    if (!pode) return;
    try {
      const res = await axios.get(`${baseUrl}/api/instance-orders/checkout-clients`, authHeaders());
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : payload?.clients || [];
      setClientes(list);
      setIsFullSuperAdmin(isFullSuperAdminUser(userData, payload?.isFullSuperAdmin));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao carregar clientes');
      setClientes([]);
    }
  }, [pode, userData]);

  const carregarWl = useCallback(async () => {
    if (!pode || !isSuperAdmin) return;
    try {
      const res = await axios.get(`${baseUrl}/api/white-labels`, authHeaders());
      setWhiteLabels(res.data || []);
    } catch {
      setWhiteLabels([]);
    }
  }, [pode, isSuperAdmin]);

  const carregarAdminsEUsuarios = useCallback(async () => {
    if (!pode || !isSuperAdmin) return;
    try {
      const [adminsRes, usersRes] = await Promise.all([
        axios.get(`${baseUrl}/api/whatsapp-instances/assignable-admins`, authHeaders()),
        axios.get(`${baseUrl}/api/whatsapp-instances/assignable-users`, authHeaders())
      ]);
      setAdmins(adminsRes.data?.users || []);
      setAllUsers(usersRes.data?.users || []);
    } catch (e) {
      setAdmins([]);
      setAllUsers([]);
    }
  }, [pode, isSuperAdmin]);

  useEffect(() => {
    if (!pode) return;
    void carregarClientes();
    axios
      .get(`${baseUrl}/api/instance-orders/billing-info`, authHeaders())
      .then((res) => setBilling(res.data))
      .catch(() => setBilling({ price: 10, billingCycleLabel: 'mensal' }));
  }, [pode, carregarClientes]);

  useEffect(() => {
    if (!pode || !isSuperAdmin) return;
    void carregarWl();
    void carregarAdminsEUsuarios();
  }, [pode, isSuperAdmin, carregarWl, carregarAdminsEUsuarios]);

  const criarParaAdmin = async () => {
    if (!isSuperAdmin) return;
    if (!adminCreateId) {
      toast.error('Selecione o admin');
      return;
    }
    if (!adminCreateNome.trim()) {
      toast.error('Informe um nome para a instância');
      return;
    }
    setAdminCreateLoading(true);
    try {
      await axios.post(
        `${baseUrl}/api/whatsapp-instances`,
        { displayName: adminCreateNome.trim(), targetUserId: adminCreateId },
        authHeaders()
      );
      toast.success('Instância criada e vinculada ao admin. Já aparece no painel dele.');
      setAdminCreateNome('');
      setAdminCreateId('');
      void carregarInstancias();
      setTab('instancias');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Erro ao criar instância para o admin');
    } finally {
      setAdminCreateLoading(false);
    }
  };

  const criarParaMim = async () => {
    if (!podeCriarGratis) return;
    const meuId = userData.id || userData._id;
    if (!meuId) {
      toast.error('Sessão sem ID de usuário. Faça login novamente.');
      return;
    }
    if (!selfCreateNome.trim()) {
      toast.error('Informe um nome para a instância');
      return;
    }
    setSelfCreateLoading(true);
    try {
      await axios.post(
        `${baseUrl}/api/whatsapp-instances`,
        { displayName: selfCreateNome.trim(), targetUserId: meuId },
        authHeaders()
      );
      toast.success('Instância criada e vinculada à sua conta.');
      setSelfCreateNome('');
      void carregarInstancias();
      setTab('instancias');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Erro ao criar instância para mim');
    } finally {
      setSelfCreateLoading(false);
    }
  };

  const qsInstancias = (opts = {}) => {
    const p = new URLSearchParams();
    const page = opts.page ?? pagina;
    const limit = opts.limit ?? itensPorPagina;
    p.set('page', String(page));
    p.set('limit', String(limit));
    if (filtroStatus) p.set('status', filtroStatus);
    if (filtroSearch) p.set('search', filtroSearch);
    if (filtroToken) p.set('token', filtroToken.trim());
    if (filtroClienteId) p.set('userId', filtroClienteId);
    if (isSuperAdmin && filtroWlId) p.set('whiteLabelId', filtroWlId);
    if (tab === 'instancias') {
      if (filtroDataIni) p.set('dateFrom', filtroDataIni);
      if (filtroDataFim) p.set('dateTo', filtroDataFim);
    }
    return `?${p.toString()}`;
  };

  const qsPedidos = () => {
    const p = new URLSearchParams();
    if (filtroStatus) p.set('status', filtroStatus);
    if (filtroSearch) p.set('search', filtroSearch);
    if (filtroClienteId) p.set('userId', filtroClienteId);
    if (isSuperAdmin && filtroWlId) p.set('whiteLabelId', filtroWlId);
    const s = p.toString();
    return s ? `?${s}` : '';
  };

  const carregarInstancias = async (opts = {}) => {
    if (!pode) return;
    const page = opts.page ?? pagina;
    const limit = opts.limit ?? itensPorPagina;
    try {
      setLoading(true);
      const res = await axios.get(
        `${baseUrl}/api/whatsapp-instances/uazapi-catalog${qsInstancias({ page, limit })}`,
        authHeaders()
      );
      setInstancias(res.data?.items || []);
      const total = res.data?.total ?? 0;
      const totalPages = res.data?.totalPages ?? 1;
      const currentPage = res.data?.page ?? page;
      setCatalogMeta({
        total,
        totalPages,
        page: currentPage,
        uazapiSourceCount: res.data?.uazapiSourceCount ?? null,
        uazapiBaseHost: res.data?.uazapiBaseHost ?? null
      });
      setPagina(currentPage);
      if (res.data?.isFullSuperAdmin !== undefined) {
        setIsFullSuperAdmin(isFullSuperAdminUser(userData, res.data.isFullSuperAdmin));
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao listar instâncias');
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltrosInstancias = () => {
    setPagina(1);
    void carregarInstancias({ page: 1 });
  };

  const irParaPagina = (novaPagina) => {
    const p = Math.max(1, Math.min(novaPagina, catalogMeta.totalPages || 1));
    setPagina(p);
    void carregarInstancias({ page: p });
  };

  const alterarItensPorPagina = (novoLimite) => {
    setItensPorPagina(novoLimite);
    setPagina(1);
    void carregarInstancias({ page: 1, limit: novoLimite });
  };

  const carregarPedidos = async () => {
    if (!pode) return;
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/api/instance-orders${qsPedidos()}`, authHeaders());
      setPedidos(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao listar pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!pode) return;
    if (tab === 'instancias') void carregarInstancias();
    else if (tab === 'pedidos') void carregarPedidos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, pode]);

  const criarGratis = async () => {
    if (!podeCriarGratis) return;
    if (!clienteCheckoutId) {
      toast.error('Selecione o cliente');
      return;
    }
    if (!novaNome.trim()) {
      toast.error('Informe um nome para a instância');
      return;
    }
    setCheckoutLoading(true);
    try {
      // Super admin: grant-free (clientes). White label admin: POST / (clientes do próprio white label).
      if (isSuperAdmin) {
        await axios.post(
          `${baseUrl}/api/instance-orders/grant-free`,
          { displayName: novaNome.trim(), targetUserId: clienteCheckoutId },
          authHeaders()
        );
      } else {
        await axios.post(
          `${baseUrl}/api/whatsapp-instances`,
          { displayName: novaNome.trim(), targetUserId: clienteCheckoutId },
          authHeaders()
        );
      }
      toast.success('Instância criada gratuitamente e vinculada ao cliente.');
      setNovaNome('');
      setClienteCheckoutId('');
      void carregarInstancias();
      void carregarPedidos();
      setTab('instancias');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Erro ao criar instância gratuita');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const checkout = async () => {
    if (!clienteCheckoutId) {
      toast.error('Selecione o cliente');
      return;
    }
    if (!novaNome.trim()) {
      toast.error('Informe um nome para a instância');
      return;
    }
    setCheckoutLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/instance-orders/checkout`,
        { displayName: novaNome.trim(), targetUserId: clienteCheckoutId },
        authHeaders()
      );
      if (res.data.paymentMethod === 'credits') {
        toast.success('Instância criada com créditos do cliente.');
        setNovaNome('');
        setClienteCheckoutId('');
        void carregarInstancias();
        setTab('instancias');
        return;
      }
      const url = res.data.paymentUrl;
      if (url) {
        const opened = window.open(url, '_blank', 'noopener,noreferrer');
        if (!opened) {
          toast.error('Pop-up bloqueado. Abra o link na aba Pedidos.');
        } else {
          toast.success('Link de pagamento aberto. Após pagar, sincronize na aba Pedidos.');
        }
      } else {
        toast.success('Cobrança criada. Sincronize o pagamento na aba Pedidos.');
      }
      setNovaNome('');
      setTab('pedidos');
      void carregarPedidos();
    } catch (e) {
      if (e.code === 'ECONNABORTED') {
        toast.error('Tempo esgotado ao falar com a API. Confira se o backend está em http://localhost:3000.');
      } else if (e.message === 'Network Error') {
        toast.error(`Sem conexão com a API (${baseUrl}). Verifique REACT_APP_API_BASE_URL e se o backend está rodando.`);
      } else {
        toast.error(e.response?.data?.message || e.message || 'Erro ao criar instância');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const syncPaymentAsaas = async (orderId) => {
    if (!orderId) return;
    try {
      setSyncPayLoadingId(orderId);
      const res = await axios.post(
        `${baseUrl}/api/instance-orders/sync-payment`,
        { orderId },
        authHeaders()
      );
      const d = res.data;
      if (d.skipped && d.reason === 'payment_not_confirmed_yet') {
        toast(`Asaas ainda não marcou como pago (status: ${d.asaasStatus || '—'}).`);
        return;
      }
      if (d.error === 'uazapi_init' || d.error === 'no_token') {
        toast.error(d.message || 'Pagamento ok, mas falhou na UAZAPI.');
        return;
      }
      if (d.instance || d.skipped?.reason === 'already_fulfilled') {
        toast.success('Instância criada ou já existente.');
        void carregarInstancias();
        void carregarPedidos();
        return;
      }
      toast.success('Sincronizado.');
      void carregarPedidos();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao sincronizar');
    } finally {
      setSyncPayLoadingId(null);
    }
  };

  const reprocessar = async (id) => {
    if (!window.confirm('Reprocessar criação para esta instância?')) return;
    try {
      const res = await axios.post(
        `${baseUrl}/api/whatsapp-instances/${id}/reprocess-creation`,
        {},
        authHeaders()
      );
      toast.success(JSON.stringify(res.data));
      await carregarInstancias();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro');
    }
  };

  const disconnect = async (inst) => {
    const label = inst.name || inst.uazapiInstanceId || inst._id || 'instância';
    if (!window.confirm(`Desconectar WhatsApp da instância "${label}"?`)) return;
    try {
      if (inst._id) {
        await axios.post(`${baseUrl}/api/whatsapp-instances/${inst._id}/disconnect`, {}, authHeaders());
      } else if (inst.uazapiToken) {
        await axios.post(
          `${baseUrl}/api/whatsapp-instances/uazapi-token/disconnect`,
          { token: inst.uazapiToken },
          authHeaders()
        );
      } else {
        toast.error('Instância sem identificador para desconectar.');
        return;
      }
      toast.success('Instância desconectada.');
      const modalKey = inst._id || inst.uazapiToken;
      if (connectionModal?.key === modalKey) setConnectionModal(null);
      await carregarInstancias();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao desconectar');
    }
  };

  const apagar = async (inst) => {
    const label = inst.name || inst.uazapiInstanceId || inst._id || 'instância';
    if (!window.confirm(`Apagar permanentemente "${label}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      if (inst._id) {
        await axios.delete(`${baseUrl}/api/whatsapp-instances/${inst._id}`, authHeaders());
      } else if (inst.uazapiToken) {
        await axios.delete(`${baseUrl}/api/whatsapp-instances/uazapi-token`, {
          ...authHeaders(),
          data: { token: inst.uazapiToken }
        });
      } else {
        toast.error('Instância sem identificador para apagar.');
        return;
      }
      toast.success('Instância removida.');
      const modalKey = inst._id || inst.uazapiToken;
      if (connectionModal?.key === modalKey) setConnectionModal(null);
      await carregarInstancias();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao apagar');
    }
  };

  const retryFulfillment = async (orderId) => {
    try {
      const res = await axios.post(
        `${baseUrl}/api/instance-orders/${orderId}/retry-fulfillment`,
        {},
        authHeaders()
      );
      toast.success(JSON.stringify(res.data));
      await carregarPedidos();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro');
    }
  };

  if (!pode) {
    return <p className="text-muted-foreground">Sem permissão para esta área.</p>;
  }

  const openConnectionUi = (inst) => {
    setConnectionModal({
      key: inst._id || inst.uazapiToken,
      id: inst._id || null,
      uazapiToken: inst.uazapiToken || null,
      name: inst.name,
      initialQr: inst.connectionQrCode || null,
      initialPairing: inst.connectionPairingCode || null
    });
  };

  const openDetail = (inst) => {
    setDetailModal(inst);
  };

  const openLinkModal = (inst) => {
    setLinkModal(inst);
    setLinkClienteId(inst.userId || '');
    setLinkNome(inst.name || inst.profileName || '');
  };

  const confirmarAssociacao = async () => {
    if (!linkModal?.uazapiToken) {
      toast.error('Instância sem token.');
      return;
    }
    if (!linkClienteId) {
      toast.error('Selecione o usuário');
      return;
    }
    setLinkLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/whatsapp-instances/link-to-client`,
        {
          uazapiToken: linkModal.uazapiToken,
          targetUserId: linkClienteId,
          displayName: linkNome.trim() || undefined
        },
        authHeaders()
      );
      const msg = res.data.reassigned
        ? 'Instância reassociada ao usuário.'
        : 'Instância associada ao usuário. Ela já aparece no painel dele.';
      toast.success(msg);
      setLinkModal(null);
      setLinkClienteId('');
      setLinkNome('');
      if (detailModal?.uazapiToken === linkModal.uazapiToken) setDetailModal(null);
      void carregarInstancias();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao associar instância');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleDetailConnect = (inst) => {
    setDetailModal(null);
    openConnectionUi(inst);
  };

  const handleDetailDisconnect = async (inst) => {
    setDetailActionLoading(true);
    try {
      await disconnect(inst);
      setDetailModal(null);
    } finally {
      setDetailActionLoading(false);
    }
  };

  const stopRowClick = (e) => e.stopPropagation();

  const rowKey = (i) => i._id || i.uazapiInstanceId || i.uazapiToken || i.name;
  const priceLabel = billingPriceLabel(billing);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <WhatsAppWebhookModal
        open={!!webhookModal}
        instance={webhookModal}
        apiBaseUrl={baseUrl}
        onClose={() => setWebhookModal(null)}
      />
      <WhatsAppInstanceDetailModal
        open={!!detailModal}
        instance={detailModal}
        apiBaseUrl={baseUrl}
        onClose={() => setDetailModal(null)}
        onConnect={handleDetailConnect}
        onDisconnect={handleDetailDisconnect}
        onConfigureWebhook={(inst) => setWebhookModal(inst)}
        onVerifyPayment={syncPaymentAsaas}
        actionLoading={detailActionLoading}
        showToken
      />
      {linkModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="link-client-title"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-lg">
            <div className="mb-4 flex items-start justify-between gap-2">
              <div>
                <h2 id="link-client-title" className="text-lg font-semibold text-foreground">
                  {linkModal.inSiriusysDb ? 'Alterar usuário' : 'Associar a um usuário'}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Vincula esta instância a qualquer usuário (admin, cliente ou comum) para que ele veja,
                  conecte e configure webhook no painel.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLinkModal(null)}
                className="rounded p-1 text-muted-foreground hover:bg-muted"
                aria-label="Fechar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-4 rounded-md bg-muted/50 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Instância: </span>
              <span className="font-medium">{linkModal.name || linkModal.profileName || '—'}</span>
              {linkModal.phoneNumber || linkModal.owner ? (
                <span className="text-muted-foreground"> · {linkModal.phoneNumber || linkModal.owner}</span>
              ) : null}
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Usuário</Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={linkClienteId}
                  onChange={(e) => setLinkClienteId(e.target.value)}
                >
                  <option value="">— Selecione o usuário —</option>
                  {allUsers.map((u) => (
                    <option key={u._id} value={u._id}>
                      {userLabel(u)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Nome no painel (opcional)</Label>
                <Input
                  placeholder="Ex.: Vendas SP"
                  value={linkNome}
                  onChange={(e) => setLinkNome(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setLinkModal(null)} disabled={linkLoading}>
                Cancelar
              </Button>
              <Button type="button" onClick={confirmarAssociacao} disabled={linkLoading}>
                {linkLoading ? 'Salvando…' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <WhatsAppConnectionModal
        open={!!connectionModal}
        instanceId={connectionModal?.id}
        uazapiToken={connectionModal?.uazapiToken}
        instanceName={connectionModal?.name || ''}
        initialQr={connectionModal?.initialQr}
        initialPairing={connectionModal?.initialPairing}
        baseUrl={baseUrl}
        onClose={() => setConnectionModal(null)}
        onConnected={() => {
          void carregarInstancias();
          setDetailModal(null);
        }}
        onDisconnected={() => {
          void carregarInstancias();
          setDetailModal(null);
        }}
      />
      <div>
        <h1 className="text-2xl font-semibold text-foreground">WhatsApp — gestão</h1>
        <p className="text-sm text-muted-foreground">
          Lista em tempo real(todas as instâncias para super admin; escopo do tenant ou white label para
          admins). Conecte, desconecte ou apague números conforme permissão.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <PlusCircleIcon className="h-5 w-5" />
            Nova instância para cliente
          </CardTitle>
          {priceLabel && (
            <p className="text-sm text-muted-foreground">
              Cobrança com assinatura Asaas: <strong>{priceLabel}</strong> por instância
            </p>
          )}
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2 sm:col-span-2">
            <Label>Cliente</Label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={clienteCheckoutId}
              onChange={(e) => setClienteCheckoutId(e.target.value)}
            >
              <option value="">— Selecione o cliente —</option>
              {clientes.map((c) => (
                <option key={c._id} value={c._id}>
                  {clientLabel(c)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Nome da instância</Label>
            <Input
              placeholder="Ex.: Vendas SP"
              value={novaNome}
              onChange={(e) => setNovaNome(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-1">
            <div className="flex flex-wrap items-end gap-2">
              {podeCriarGratis && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={criarGratis}
                  disabled={checkoutLoading}
                  title="Cria e associa ao cliente sem cobrança Asaas"
                >
                  {checkoutLoading ? 'Processando…' : 'Criar grátis'}
                </Button>
              )}
              <Button type="button" onClick={checkout} disabled={checkoutLoading}>
                {checkoutLoading
                  ? 'Processando…'
                  : priceLabel
                    ? `Criar e cobrar (${priceLabel})`
                    : 'Criar e gerar cobrança'}
              </Button>
            </div>
            {podeCriarGratis && (
              <p className="text-xs text-muted-foreground">
                <strong>Criar grátis:</strong> instância aparece no painel do cliente, sem pagamento.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PlusCircleIcon className="h-5 w-5" />
              Criar instância para um admin (grátis)
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Provisiona uma instância sem cobrança e a vincula ao admin escolhido. Ela aparece direto no
              painel dele.
            </p>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2 sm:col-span-2">
              <Label>Admin dono da instância</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={adminCreateId}
                onChange={(e) => setAdminCreateId(e.target.value)}
              >
                <option value="">— Selecione o admin —</option>
                {admins.map((u) => (
                  <option key={u._id} value={u._id}>
                    {userLabel(u)}
                  </option>
                ))}
              </select>
              {admins.length === 0 && (
                <p className="text-xs text-muted-foreground">Nenhum usuário com papel admin encontrado.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Nome da instância</Label>
              <Input
                placeholder="Ex.: Atendimento Admin"
                value={adminCreateNome}
                onChange={(e) => setAdminCreateNome(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button type="button" onClick={criarParaAdmin} disabled={adminCreateLoading}>
                {adminCreateLoading ? 'Criando…' : 'Criar para o admin'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {podeCriarGratis && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PlusCircleIcon className="h-5 w-5" />
              Criar instância para mim (grátis)
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Provisiona uma instância sem cobrança vinculada à sua própria conta.
            </p>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2 sm:col-span-2">
              <Label>Nome da instância</Label>
              <Input
                placeholder="Ex.: Meu WhatsApp"
                value={selfCreateNome}
                onChange={(e) => setSelfCreateNome(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button type="button" onClick={criarParaMim} disabled={selfCreateLoading}>
                {selfCreateLoading ? 'Criando…' : 'Criar para mim'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant={tab === 'instancias' ? 'default' : 'outline'} onClick={() => setTab('instancias')}>
          Instâncias
        </Button>
        <Button variant={tab === 'pedidos' ? 'default' : 'outline'} onClick={() => setTab('pedidos')}>
          Pedidos / pagamentos
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <Label>Cliente</Label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filtroClienteId}
              onChange={(e) => setFiltroClienteId(e.target.value)}
            >
              <option value="">— Todos os clientes —</option>
              {clientes.map((c) => (
                <option key={c._id} value={c._id}>
                  {clientLabel(c)}
                </option>
              ))}
            </select>
          </div>
          {isSuperAdmin && whiteLabels.length > 0 && (
            <div className="space-y-2">
              <Label>White label</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filtroWlId}
                onChange={(e) => setFiltroWlId(e.target.value)}
              >
                <option value="">— Todos —</option>
                {whiteLabels.map((w) => (
                  <option key={w._id} value={w._id}>
                    {w.nome}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Status</Label>
            <Input
              placeholder="ex.: connected, pending"
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Nome / número / instância / token</Label>
            <Input
              placeholder="Busca geral (inclui token parcial)"
              value={filtroSearch}
              onChange={(e) => setFiltroSearch(e.target.value)}
            />
          </div>
          {tab === 'instancias' && (
            <div className="space-y-2">
              <Label>Token</Label>
              <Input
                placeholder="UUID ou parte do token"
                value={filtroToken}
                onChange={(e) => setFiltroToken(e.target.value)}
              />
            </div>
          )}
          {tab === 'instancias' && (
            <>
              <div className="space-y-2">
                <Label>Data inicial</Label>
                <Input type="date" value={filtroDataIni} onChange={(e) => setFiltroDataIni(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Data final</Label>
                <Input type="date" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} />
              </div>
            </>
          )}
          <div className="flex items-end gap-2">
            <Button
              type="button"
              onClick={() => {
                if (tab === 'instancias') aplicarFiltrosInstancias();
                else void carregarPedidos();
              }}
              disabled={loading}
            >
              <ArrowPathIcon className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aplicar
            </Button>
          </div>
        </CardContent>
      </Card>

      {tab === 'instancias' && (
        <Card>
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>
              Instâncias ({catalogMeta.total})
              {catalogMeta.total > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  página {catalogMeta.page} de {catalogMeta.totalPages}
                </span>
              )}
              {isSuperAdmin && catalogMeta.uazapiSourceCount != null && (
                <span className="mt-1 block text-xs font-normal text-muted-foreground">
                  UAZAPI retornou {catalogMeta.uazapiSourceCount} instância(s)
                  {catalogMeta.uazapiBaseHost ? ` em ${catalogMeta.uazapiBaseHost}` : ''}
                </span>
              )}
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="itens-por-pagina" className="text-sm text-muted-foreground">
                Por página
              </Label>
              <select
                id="itens-por-pagina"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={itensPorPagina}
                onChange={(e) => alterarItensPorPagina(Number(e.target.value))}
                disabled={loading}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2">Nome</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Telefone</th>
                  <th className="p-2">Cliente</th>
                  <th className="p-2">Origem</th>
                  <th className="p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {instancias.map((i) => (
                  <tr
                    key={rowKey(i)}
                    className="cursor-pointer border-b border-border/40 transition-colors hover:bg-accent/40"
                    onClick={() => openDetail(i)}
                  >
                    <td className="p-2">
                      <div>{i.name}</div>
                      {i.profileName && i.profileName !== i.name && (
                        <div className="text-xs text-muted-foreground">{i.profileName}</div>
                      )}
                    </td>
                    <td className="p-2">
                      <Badge variant="outline">{i.status || i.uazapiStatus || '—'}</Badge>
                    </td>
                    <td className="p-2">{i.phoneNumber || i.owner || '—'}</td>
                    <td className="p-2">
                      <div className="font-medium">{i.userNome || '—'}</div>
                      {i.userEmail && <div className="text-xs text-muted-foreground">{i.userEmail}</div>}
                    </td>
                    <td className="p-2">
                      <Badge variant={i.inSiriusysDb ? 'secondary' : 'outline'}>
                        {i.inSiriusysDb ? 'Siriusys' : 'Só UAZAPI'}
                      </Badge>
                    </td>
                    <td className="p-2 flex flex-wrap gap-2" onClick={stopRowClick}>
                      {i.status !== 'connected' && i.uazapiToken && (
                        <Button type="button" variant="default" size="sm" onClick={() => openConnectionUi(i)}>
                          Conectar QR
                        </Button>
                      )}
                      {(i.status === 'connected' || i.status === 'waiting_connection') && i.uazapiToken && (
                        <Button type="button" variant="outline" size="sm" onClick={() => disconnect(i)}>
                          <StopIcon className="mr-1 h-4 w-4" />
                          Desconectar
                        </Button>
                      )}
                      {isSuperAdmin && i.uazapiToken && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => openLinkModal(i)}
                          title={
                            i.inSiriusysDb
                              ? 'Alterar usuário vinculado'
                              : 'Registrar no Siriusys e vincular a um usuário'
                          }
                        >
                          <LinkIcon className="mr-1 h-4 w-4" />
                          {i.inSiriusysDb ? 'Alterar usuário' : 'Associar usuário'}
                        </Button>
                      )}
                      {i.uazapiToken && (
                        <Button type="button" variant="destructive" size="sm" onClick={() => apagar(i)}>
                          Apagar
                        </Button>
                      )}
                      {isSuperAdmin && i._id && (
                        <Button type="button" variant="outline" size="sm" onClick={() => reprocessar(i._id)}>
                          Reprocessar UAZ
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {catalogMeta.total > 0 && (
              <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Mostrando {(catalogMeta.page - 1) * itensPorPagina + 1}–
                  {Math.min(catalogMeta.page * itensPorPagina, catalogMeta.total)} de {catalogMeta.total}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={loading || catalogMeta.page <= 1}
                    onClick={() => irParaPagina(catalogMeta.page - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="px-2 text-sm text-muted-foreground">
                    {catalogMeta.page} / {catalogMeta.totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={loading || catalogMeta.page >= catalogMeta.totalPages}
                    onClick={() => irParaPagina(catalogMeta.page + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'pedidos' && (
        <Card>
          <CardHeader>
            <CardTitle>Pedidos ({pedidos.length})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2">Nome</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Valor</th>
                  <th className="p-2">Cliente</th>
                  <th className="p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((o) => (
                  <tr key={o._id} className="border-b border-border/40">
                    <td className="p-2">{o.displayName || o.instanceName}</td>
                    <td className="p-2">
                      <Badge variant="outline">{o.status}</Badge>
                    </td>
                    <td className="p-2">
                      {o.amount != null ? formatBrl(o.amount) : '—'}
                      {o.billingCycle === 'MONTHLY' && o.amount > 0 ? (
                        <span className="text-xs text-muted-foreground"> /mês</span>
                      ) : null}
                    </td>
                    <td className="p-2">
                      <div className="font-medium">{o.userNome || '—'}</div>
                      {o.userEmail && <div className="text-xs text-muted-foreground">{o.userEmail}</div>}
                    </td>
                    <td className="p-2 flex flex-wrap gap-2">
                      {o.status === 'pending' && o.paymentMethod === 'asaas' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={syncPayLoadingId === o._id}
                          onClick={() => syncPaymentAsaas(o._id)}
                        >
                          {syncPayLoadingId === o._id ? '…' : 'Já pagou'}
                        </Button>
                      )}
                      {isSuperAdmin && o.status === 'paid' && (
                        <Button type="button" variant="outline" size="sm" onClick={() => retryFulfillment(o._id)}>
                          Retry fulfillment
                        </Button>
                      )}
                      {o.paymentUrl && (
                        <a
                          className="text-primary underline text-sm self-center"
                          href={o.paymentUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Link pagamento
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Visão do ADMIN comum: só as próprias instâncias + criar instância para si mesmo (grátis).
 * Sem seletor de usuário, sem associação, sem catálogo de terceiros.
 */
function AdminSelfInstanciasView() {
  const [instancias, setInstancias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [novaNome, setNovaNome] = useState('');
  const [criando, setCriando] = useState(false);
  const [connectionModal, setConnectionModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [webhookModal, setWebhookModal] = useState(null);
  const [detailActionLoading, setDetailActionLoading] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/api/whatsapp-instances`, authHeaders());
      setInstancias(Array.isArray(res.data) ? res.data : res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao listar instâncias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const criarParaMim = async () => {
    if (!novaNome.trim()) {
      toast.error('Informe um nome para a instância');
      return;
    }
    setCriando(true);
    try {
      await axios.post(`${baseUrl}/api/whatsapp-instances`, { displayName: novaNome.trim() }, authHeaders());
      toast.success('Instância criada para você.');
      setNovaNome('');
      void carregar();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Erro ao criar instância');
    } finally {
      setCriando(false);
    }
  };

  const openConnectionUi = (inst) => {
    setConnectionModal({
      key: inst._id,
      id: inst._id,
      name: inst.name,
      initialQr: inst.connectionQrCode || null,
      initialPairing: inst.connectionPairingCode || null
    });
  };

  const openDetail = async (inst) => {
    try {
      const res = await axios.get(`${baseUrl}/api/whatsapp-instances/${inst._id}/detail`, authHeaders());
      setDetailModal(res.data);
    } catch (e) {
      // Mesmo sem o detalhe ao vivo, abre com o que já temos.
      setDetailModal(inst);
    }
  };

  const disconnect = async (inst) => {
    if (!window.confirm(`Desconectar WhatsApp da instância "${inst.name || ''}"?`)) return;
    try {
      await axios.post(`${baseUrl}/api/whatsapp-instances/${inst._id}/disconnect`, {}, authHeaders());
      toast.success('Instância desconectada.');
      void carregar();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao desconectar');
    }
  };

  const apagar = async (inst) => {
    if (!window.confirm(`Apagar permanentemente "${inst.name || ''}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${baseUrl}/api/whatsapp-instances/${inst._id}`, authHeaders());
      toast.success('Instância removida.');
      void carregar();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao apagar');
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <WhatsAppWebhookModal
        open={!!webhookModal}
        instance={webhookModal}
        apiBaseUrl={baseUrl}
        onClose={() => setWebhookModal(null)}
      />
      <WhatsAppInstanceDetailModal
        open={!!detailModal}
        instance={detailModal}
        apiBaseUrl={baseUrl}
        onClose={() => setDetailModal(null)}
        onConnect={(inst) => {
          setDetailModal(null);
          openConnectionUi(inst);
        }}
        onDisconnect={async (inst) => {
          setDetailActionLoading(true);
          try {
            await disconnect(inst);
            setDetailModal(null);
          } finally {
            setDetailActionLoading(false);
          }
        }}
        onConfigureWebhook={(inst) => setWebhookModal(inst)}
        actionLoading={detailActionLoading}
        showToken
      />
      <WhatsAppConnectionModal
        open={!!connectionModal}
        instanceId={connectionModal?.id}
        instanceName={connectionModal?.name || ''}
        initialQr={connectionModal?.initialQr}
        initialPairing={connectionModal?.initialPairing}
        baseUrl={baseUrl}
        onClose={() => setConnectionModal(null)}
        onConnected={() => void carregar()}
        onDisconnected={() => void carregar()}
      />

      <div>
        <h1 className="text-2xl font-semibold text-foreground">Instâncias WhatsApp</h1>
        <p className="text-sm text-muted-foreground">
          Suas instâncias e as dos seus clientes. Crie a sua, conecte o telefone, veja token e status.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <PlusCircleIcon className="h-5 w-5" />
            Criar instância para mim
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <Label>Nome da instância</Label>
            <Input
              placeholder="Ex.: Meu WhatsApp"
              value={novaNome}
              onChange={(e) => setNovaNome(e.target.value)}
            />
          </div>
          <Button type="button" onClick={criarParaMim} disabled={criando}>
            {criando ? 'Criando…' : 'Criar instância'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Instâncias ({instancias.length})</CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={() => void carregar()} disabled={loading}>
            <ArrowPathIcon className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {instancias.length === 0 ? (
            <p className="text-sm text-muted-foreground">Você ainda não tem instâncias. Crie a primeira acima.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2">Nome</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Telefone</th>
                  <th className="p-2">Cliente / dono</th>
                  <th className="p-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {instancias.map((i) => (
                  <tr
                    key={i._id}
                    className="cursor-pointer border-b border-border/40 transition-colors hover:bg-accent/40"
                    onClick={() => openDetail(i)}
                  >
                    <td className="p-2">{i.name}</td>
                    <td className="p-2">
                      <Badge variant="outline">{i.status || '—'}</Badge>
                    </td>
                    <td className="p-2">{i.phoneNumber || '—'}</td>
                    <td className="p-2">
                      <div className="font-medium">{i.userNome || '—'}</div>
                      {i.userEmail && <div className="text-xs text-muted-foreground">{i.userEmail}</div>}
                    </td>
                    <td className="p-2 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                      {i.status !== 'connected' && (
                        <Button type="button" variant="default" size="sm" onClick={() => openConnectionUi(i)}>
                          Conectar QR
                        </Button>
                      )}
                      {(i.status === 'connected' || i.status === 'waiting_connection') && (
                        <Button type="button" variant="outline" size="sm" onClick={() => disconnect(i)}>
                          <StopIcon className="mr-1 h-4 w-4" />
                          Desconectar
                        </Button>
                      )}
                      <Button type="button" variant="destructive" size="sm" onClick={() => apagar(i)}>
                        Apagar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function WhatsAppInstanciasAdmin() {
  const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || '{}'), []);
  const effRole = normalizeRole(userData.role);
  const rawRole = String(userData.role || '').toLowerCase();
  const isFull = isFullSuperAdminUser(userData, null);

  // Admin comum (não super, não white label) → visão restrita às próprias instâncias.
  if (!isFull && rawRole === 'admin') {
    return <AdminSelfInstanciasView />;
  }
  // Super admin e white label → visão de gestão completa.
  if (isFull || effRole === 'super_admin' || effRole === 'white_label_admin') {
    return <SuperAdminInstanciasView />;
  }
  return <p className="text-muted-foreground">Sem permissão para esta área.</p>;
}
